import useMediaQuery from '@mui/material/useMediaQuery';
import {
  type CSSProperties,
  cloneElement,
  forwardRef,
  type ReactElement,
  type Ref,
  useMemo,
  useRef,
} from 'react';
import { Transition, type TransitionStatus } from 'react-transition-group';

export const MENU_TRANSITION_EASING = 'cubic-bezier(0.23, 1, 0.32, 1)';
export const MENU_TRANSITION_DURATION = { enter: 160, exit: 120 };

const ENTER_SCALE = 0.96;

function forceReflow(node: HTMLElement) {
  void node.offsetHeight;
}

type TimeoutValue = number | { appear?: number; enter?: number; exit?: number };
type EnterCallback = (node: HTMLElement | null, isAppearing: boolean) => void;
type NodeCallback = (node: HTMLElement | null) => void;
type NodeRef = Ref<HTMLElement> | undefined;

interface MenuTransitionProps {
  in?: boolean;
  appear?: boolean;
  children: ReactElement<{ style?: CSSProperties; ref?: NodeRef }>;
  style?: CSSProperties;
  timeout?: TimeoutValue;
  onEnter?: EnterCallback;
  onEntering?: EnterCallback;
  onEntered?: EnterCallback;
  onExit?: NodeCallback;
  onExiting?: NodeCallback;
  onExited?: NodeCallback;
  addEndListener?: (node: HTMLElement, done: () => void) => void;
}

function setRef(ref: NodeRef, value: HTMLElement | null) {
  if (typeof ref === 'function') ref(value);
  else if (ref) (ref as { current: HTMLElement | null }).current = value;
}

function useForkRef(a: NodeRef, b: NodeRef, c: NodeRef) {
  return useMemo(
    () => (value: HTMLElement | null) => {
      setRef(a, value);
      setRef(b, value);
      setRef(c, value);
    },
    [a, b, c],
  );
}

function getElementRef(element: ReactElement<{ ref?: NodeRef }>): NodeRef {
  return (
    (element.props as { ref?: NodeRef }).ref ??
    (element as { ref?: NodeRef }).ref
  );
}

export const MenuTransition = forwardRef<unknown, MenuTransitionProps>(
  function MenuTransition(props, ref) {
    const {
      children,
      in: inProp,
      appear = true,
      style,
      timeout = MENU_TRANSITION_DURATION,
      onEnter,
      onEntering,
      onEntered,
      onExit,
      onExiting,
      onExited,
      addEndListener,
      ...other
    } = props;

    const nodeRef = useRef<HTMLElement>(null);
    const handleRef = useForkRef(
      nodeRef,
      getElementRef(children),
      ref as NodeRef,
    );
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)', {
      noSsr: true,
    });
    const scale = reduceMotion ? 1 : ENTER_SCALE;

    const applyTransition = (mode: 'enter' | 'exit') => {
      const node = nodeRef.current;
      if (!node) return;
      const duration = reduceMotion ? 0 : MENU_TRANSITION_DURATION[mode];
      node.style.transition = `opacity ${duration}ms ${MENU_TRANSITION_EASING}, transform ${duration}ms ${MENU_TRANSITION_EASING}`;
    };

    const handleEnter = (isAppearing: boolean) => {
      const node = nodeRef.current;
      if (node) forceReflow(node);
      applyTransition('enter');
      onEnter?.(node, isAppearing);
    };

    const handleEntering = (isAppearing: boolean) =>
      onEntering?.(nodeRef.current, isAppearing);
    const handleEntered = (isAppearing: boolean) =>
      onEntered?.(nodeRef.current, isAppearing);

    const handleExit = () => {
      applyTransition('exit');
      onExit?.(nodeRef.current);
    };
    const handleExiting = () => onExiting?.(nodeRef.current);
    const handleExited = () => {
      const node = nodeRef.current;
      if (node) node.style.transition = '';
      onExited?.(node);
    };

    const handleAddEndListener = (done: () => void) => {
      if (addEndListener && nodeRef.current) {
        addEndListener(nodeRef.current, done);
      }
    };

    return (
      <Transition
        appear={appear}
        in={inProp}
        nodeRef={nodeRef}
        timeout={timeout}
        onEnter={handleEnter}
        onEntering={handleEntering}
        onEntered={handleEntered}
        onExit={handleExit}
        onExiting={handleExiting}
        onExited={handleExited}
        addEndListener={handleAddEndListener}
        {...other}
      >
        {(state: TransitionStatus) => {
          const entered = state === 'entering' || state === 'entered';
          return cloneElement(children, {
            ref: handleRef,
            style: {
              ...style,
              ...children.props.style,
              opacity: entered ? 1 : 0,
              transform: entered ? 'none' : `scale(${scale})`,
              ...(state === 'exited' && !inProp
                ? { visibility: 'hidden' as const }
                : null),
            },
          });
        }}
      </Transition>
    );
  },
);
